/**
 * SEO content data for Bison Food Solutions Ltd.
 * All landing pages read from these arrays so we have a single source of truth.
 */

export const COMPANY = {
  name: 'Bison Food Solutions Ltd',
  slogan: 'Fresh. Reliable. Trusted.',
  site: 'https://bison.rw',
  description:
    "Rwanda's trusted food supplier delivering fresh fruits, vegetables, meat, poultry, seafood, dairy products and dry goods to hotels, restaurants, cafés, catering companies, supermarkets, schools, hospitals and households.",
  address: {
    street: 'Muhima, Nyarugenge',
    city: 'Kigali',
    region: 'Kigali City',
    country: 'Rwanda',
    postalCode: '',
    lat: -1.9497,
    lng: 30.0562,
  },
  phone: '+250791687658',
  phoneDisplay: '+250 791 687 658',
  whatsapp: '250791687658',
  email: 'sales@bison.rw',
  hours: 'Mon–Sat 07:00 – 19:00',
  socials: {
    linkedin: 'https://www.linkedin.com/company/bison-food-solutions',
    instagram: 'https://www.instagram.com/bisonfoodsolutions',
  },
};

export const INDUSTRIES = [
  {
    slug: 'hotels',
    name: 'Hotels',
    tagline: 'Consistent quality for hospitality kitchens',
    hero: 'Reliable food supply for 3-star to 5-star hotels across Rwanda — from breakfast buffets to fine-dining kitchens.',
    benefits: [
      'Daily fresh deliveries with cold-chain integrity',
      'Consistent portioning and grade-A produce',
      'Dedicated account manager for your executive chef',
      'Flexible payment terms for hotel groups',
    ],
    faqs: [
      { q: 'Do you deliver early enough for breakfast prep?', a: 'Yes — we offer pre-6 AM deliveries for Kigali hotels on request.' },
      { q: 'Can you match seasonal menu changes?', a: 'Absolutely. Our chef-liaison team plans seasonal produce rotations quarterly.' },
    ],
  },
  {
    slug: 'restaurants',
    name: 'Restaurants',
    tagline: 'Fresh ingredients that make your menu shine',
    hero: 'Reliable supply of fresh produce, quality meats and specialty ingredients for restaurants of every cuisine.',
    benefits: [
      'Same-day and next-day delivery windows',
      'Speciality ingredients on request (Asian, Mediterranean, African)',
      'No minimum order — start with what you need',
      'Sustainable, locally sourced whenever possible',
    ],
    faqs: [
      { q: 'What is the minimum order?', a: 'None — we deliver from small orders to full weekly restocks.' },
      { q: 'Do you handle imported ingredients?', a: 'Yes, we source imported dry goods and specialty items in addition to fresh local produce.' },
    ],
  },
  {
    slug: 'cafes',
    name: 'Cafés',
    tagline: 'Coffee-shop essentials, always fresh',
    hero: 'Pastry-grade dairy, fresh fruit for smoothies, bakery flour, herbs, and everything a great café needs.',
    benefits: [
      'Small-batch, high-frequency deliveries',
      'Premium dairy for barista-quality drinks',
      'Fresh baked-goods ingredients',
    ],
    faqs: [
      { q: 'Do you supply pastry-grade butter?', a: 'Yes — we stock 82% fat pastry butter and unsalted baking butter.' },
      { q: 'Can you deliver early morning before opening?', a: 'Yes — we offer 5–7 AM deliveries for cafés so your day starts fully stocked.' },
    ],
  },
  {
    slug: 'bars',
    name: 'Bars & Lounges',
    tagline: 'Garnish-grade produce & bar snacks',
    hero: 'Fresh citrus, herbs, and quality bar snacks delivered on schedule for busy nightlife venues.',
    benefits: ['Late-morning delivery windows', 'Extended-shelf-life citrus & garnishes', 'Snacks and dry-goods in one order'],
    faqs: [
      { q: 'Do you supply garnish greens?', a: 'Yes — fresh mint, basil, rosemary and edible flowers available on order.' },
      { q: 'How fresh is your citrus?', a: 'Citrus is harvested within 48 hours of delivery — perfect for cocktail garnishes.' },
    ],
  },
  {
    slug: 'catering',
    name: 'Catering Companies',
    tagline: 'Scale up without supply headaches',
    hero: 'From 50-person dinners to 5,000-guest events, we supply the volume, freshness and consistency you need.',
    benefits: ['Event-scale volume orders', 'Guaranteed on-time delivery', 'Multi-site same-day drops'],
    faqs: [
      { q: 'Can you handle multi-day events?', a: 'Yes — we schedule staggered deliveries so nothing spoils.' },
      { q: 'What is the lead time for large events?', a: 'For events over 500 guests we need 5 days notice; smaller events can be booked same-week.' },
    ],
  },
  {
    slug: 'supermarkets',
    name: 'Supermarkets',
    tagline: 'Wholesale supply that keeps shelves full',
    hero: 'Wholesale fresh produce, dairy, dry goods and imported staples for grocery retailers across Rwanda.',
    benefits: ['Wholesale pricing', 'Consistent stock replenishment', 'Merchandising-ready pack sizes'],
    faqs: [
      { q: 'Do you deliver in refrigerated trucks?', a: 'Yes — all perishables travel in temperature-controlled vehicles.' },
      { q: 'Can we set up automatic replenishment?', a: 'Yes — we build a par-level restock schedule so shelves never run empty.' },
    ],
  },
  {
    slug: 'schools',
    name: 'Schools',
    tagline: 'Nutritious food for growing minds',
    hero: 'Safe, nutrient-rich food supply for boarding schools, day schools and cafeterias — with strict food-safety standards.',
    benefits: ['HACCP-aligned handling', 'Balanced nutrition menus available', 'Bulk pricing for institutional kitchens'],
    faqs: [
      { q: 'Do you meet MINEDUC nutrition guidelines?', a: 'Yes — our institutional menu templates align with Rwandan school-nutrition standards.' },
      { q: 'Can you handle allergen-free requirements?', a: 'Yes — we segregate allergen-free items and label deliveries clearly for kitchen staff.' },
    ],
  },
  {
    slug: 'hospitals',
    name: 'Hospitals',
    tagline: 'Safe, hygienic food for patient care',
    hero: 'Compliant, hygienic food supply for hospital kitchens, patient meals and staff canteens.',
    benefits: ['Strict cold-chain compliance', 'Traceable sourcing', 'Special dietary items on request'],
    faqs: [
      { q: 'Can you supply special-diet items?', a: 'Yes — diabetic, low-sodium and allergen-free items are available with lead time.' },
      { q: 'Do you follow HACCP protocols?', a: 'Yes — our cold chain and handling follow HACCP; we can share our SOP on request.' },
    ],
  },
  {
    slug: 'households',
    name: 'Households',
    tagline: 'Restaurant-quality produce at your door',
    hero: 'Skip the market queues — order the same fresh produce our top restaurants use, delivered to your home.',
    benefits: ['Order via the online shop', 'Weekly recurring baskets', 'Cash, MoMo, or card at delivery'],
    faqs: [
      { q: 'Where can I place a household order?', a: 'Use our online shop at /shop — free delivery in Kigali above RWF 20,000.' },
      { q: 'Do you offer a weekly subscription?', a: 'Yes — set up a weekly basket and we deliver on the same day each week.' },
    ],
    ctaLink: '/shop',
    ctaLabel: 'Shop Online',
  },
];

export const PRODUCT_CATEGORIES = [
  {
    slug: 'vegetables',
    name: 'Fresh Vegetables',
    tagline: 'Farm-fresh vegetables, harvested for you',
    hero: 'A daily-updated selection of over 60 fresh vegetables sourced from certified Rwandan farms.',
    highlights: ['Tomatoes, onions, carrots, potatoes', 'Leafy greens: sukuma, spinach, kale', 'Peppers, eggplants, cabbage, cucumbers'],
  },
  {
    slug: 'fruits',
    name: 'Fresh Fruits',
    tagline: 'Seasonal fruits at their peak',
    hero: 'Ripe, sweet fruits picked in season for maximum freshness and flavour.',
    highlights: ['Bananas, avocados, mangoes, passion fruits', 'Pineapples, watermelons, papayas', 'Berries and imported apples on order'],
  },
  {
    slug: 'meat',
    name: 'Meat',
    tagline: 'Butcher-grade meats, cut to your spec',
    hero: 'Premium beef, lamb, goat and pork — hand-cut to your kitchen’s specification.',
    highlights: ['Beef fillet, sirloin, ribeye, chuck', 'Lamb chops, leg, shoulder', 'Custom butcher cuts and portioning'],
  },
  {
    slug: 'poultry',
    name: 'Poultry',
    tagline: 'Fresh chicken and free-range birds',
    hero: 'Whole chickens, portioned cuts, boneless breasts, wings, and locally raised free-range poultry.',
    highlights: ['Whole birds, halves, quarters', 'Boneless breasts and thigh fillets', 'Duck and turkey on order'],
  },
  {
    slug: 'seafood',
    name: 'Seafood',
    tagline: 'Fresh and frozen seafood, delivered on ice',
    hero: 'Fresh tilapia, whitefish, prawns and imported seafood — cold-chain guaranteed.',
    highlights: ['Local tilapia and whitefish', 'Imported prawns, salmon fillets', 'Frozen calamari and mixed seafood'],
  },
  {
    slug: 'dairy',
    name: 'Dairy',
    tagline: 'Pasteurised dairy from trusted farms',
    hero: 'Fresh milk, butter, cheeses, yoghurt and cream — pasteurised, refrigerated, and delivered same-day.',
    highlights: ['Fresh milk (whole, low-fat)', 'Butter, cream, sour cream', 'Local cheese and imported cheeses'],
  },
  {
    slug: 'herbs',
    name: 'Herbs',
    tagline: 'Freshly cut culinary herbs',
    hero: 'Fresh basil, mint, rosemary, thyme, coriander and more — cut to order.',
    highlights: ['Basil, mint, coriander, parsley', 'Rosemary, thyme, sage, oregano', 'Available in tied bunches or bulk'],
  },
  {
    slug: 'spices',
    name: 'Spices',
    tagline: 'Whole and ground spices with rich aroma',
    hero: 'Whole and ground spices from trusted sources — freshly milled batches.',
    highlights: ['Black pepper, cardamom, cinnamon, cloves', 'Cumin, coriander, turmeric, paprika', 'Custom spice blends on request'],
  },
  {
    slug: 'dry-goods',
    name: 'Dry Goods',
    tagline: 'Pantry essentials, wholesale prices',
    hero: 'Rice, beans, lentils, sugar, salt, flour, cooking oil, pasta — the pantry backbone.',
    highlights: ['Rice, pasta, flour, sugar', 'Beans, lentils, chickpeas', 'Cooking oil, salt, canned goods'],
  },
];

export const SERVICES = [
  { slug: 'bulk-food-supply', name: 'Bulk Food Supply', hero: 'High-volume, consistent supply for institutions, events and wholesale customers.' },
  { slug: 'daily-delivery', name: 'Daily Delivery', hero: 'Reliable daily deliveries with fixed time windows — never disrupt your kitchen prep.' },
  { slug: 'hotel-food-supply', name: 'Hotel Food Supply', hero: 'Turn-key food procurement for 3–5 star hotels: fresh, dry goods, dairy and beverages.' },
  { slug: 'restaurant-food-supply', name: 'Restaurant Food Supply', hero: 'Fresh, specialty and dry goods tailored to your menu — small, mid or high volume.' },
  { slug: 'procurement-support', name: 'Procurement Support', hero: 'We consolidate suppliers, negotiate on your behalf and streamline your food spend.' },
  { slug: 'custom-orders', name: 'Custom Orders', hero: 'Rare ingredients, imported specialities, event platters — we source it for you.' },
];

export const LOCATIONS = [
  { slug: 'kigali', name: 'Kigali', hero: 'Same-day delivery across all Kigali sectors — Kacyiru, Kicukiro, Gasabo, Nyarugenge, Remera.' },
  { slug: 'musanze', name: 'Musanze', hero: 'Weekly scheduled deliveries to hotels and lodges in Musanze and the Volcanoes region.' },
  { slug: 'rubavu', name: 'Rubavu', hero: 'Fresh food supply to lakeside hotels, resorts and restaurants in Rubavu / Gisenyi.' },
  { slug: 'huye', name: 'Huye', hero: 'Southern-province deliveries to schools, institutions and hospitality in Huye.' },
  { slug: 'rwamagana', name: 'Rwamagana', hero: 'Eastern-province deliveries to Rwamagana hotels, catering companies and institutions.' },
];

export const TESTIMONIALS = [
  { name: 'Emmanuel U.', role: 'Executive Chef', company: 'Kigali Serena Hotel', rating: 5,
    text: 'Bison has been our go-to for two years. Delivery is always on time, quality never drops.' },
  { name: 'Grace M.', role: 'Procurement Manager', company: 'Simba Supermarket', rating: 5,
    text: 'Great pricing, reliable stock, and their team knows our order patterns better than we do.' },
  { name: 'Jean-Paul K.', role: 'Owner', company: 'La Table Restaurant', rating: 5,
    text: 'Their citrus and herbs are always garnish-quality. We stopped shopping around six months ago.' },
];

/** Helper: build organization JSON-LD once. */
export const organizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: COMPANY.name,
  url: COMPANY.site,
  logo: `${COMPANY.site}/logo.png`,
  description: COMPANY.description,
  telephone: COMPANY.phone,
  email: COMPANY.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: COMPANY.address.street,
    addressLocality: COMPANY.address.city,
    addressCountry: COMPANY.address.country,
  },
  sameAs: [COMPANY.socials.linkedin, COMPANY.socials.instagram],
});

export const localBusinessJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: COMPANY.name,
  description: COMPANY.description,
  image: `${COMPANY.site}/logo.png`,
  telephone: COMPANY.phone,
  email: COMPANY.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: COMPANY.address.street,
    addressLocality: COMPANY.address.city,
    addressCountry: COMPANY.address.country,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: COMPANY.address.lat,
    longitude: COMPANY.address.lng,
  },
  openingHours: 'Mo-Sa 07:00-19:00',
  priceRange: '$$',
  url: COMPANY.site,
});
